import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mui/material";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useInitiateDepositMutation } from "Billing/Api/Mutations/useInitiateDepositMutation";
import {
  type DepositFormData,
  depositFormSchema,
} from "Billing/Forms/depositFormSchema";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DepositModal = ({
  isOpen,
  onClose,
}: DepositModalProps): React.ReactNode => {
  const { t } = useResource();
  const depositMutation = useInitiateDepositMutation();
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DepositFormData>({
    resolver: zodResolver(depositFormSchema),
    defaultValues: {
      amount: undefined,
      description: "",
    },
  });

  const onSubmit = async (data: DepositFormData) => {
    try {
      const result = await depositMutation.mutateAsync({
        amount: data.amount,
        description: data.description || null,
        idempotencyKey: idempotencyKeyRef.current,
      });
      if (result.checkoutUrl) {
        window.open(result.checkoutUrl, "_blank");
      }
      toast.success(t(Resources.Billing.DepositModal.SuccessMessage));
      idempotencyKeyRef.current = crypto.randomUUID();
      reset();
      onClose();
    } catch {
      // error handled by hook
    }
  };

  const handleClose = () => {
    idempotencyKeyRef.current = crypto.randomUUID();
    reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Billing.DepositModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Billing.DepositModal.Confirm)}
      maxWidth="500px"
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={12}>
          <BlFormInput
            control={control}
            name="amount"
            label={t(Resources.Billing.DepositModal.Amount)}
            errors={errors}
            placeholder={t(Resources.Billing.DepositModal.AmountPlaceholder)}
            isNumber
            unit="€"
          />
        </Grid>
        <Grid size={12}>
          <BlFormInput
            control={control}
            name="description"
            label={t(Resources.Billing.DepositModal.Description)}
            errors={errors}
            placeholder={t(
              Resources.Billing.DepositModal.DescriptionPlaceholder,
            )}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </BlModal>
  );
};
